export const CARGO_CODES = {
  SUPERINTENDENTE: "219.840.000",
  GERENTE_VENDAS: "219.840.001",
  CORRETOR: "219.840.002",
  GERENTE_GERAL: "219.840.003",
  VENDEDOR: "219.840.004",
  SUPERVISOR: "219.840.005",
  ESPECIALISTA_PRODUTO: "219.840.006",
  RECEPCIONISTA: "219.840.007",
  CORRETOR_ALT: "219.840.012",
  GERENTE_VENDAS_ALT: "219.840.013",
} as const;

export const isCorretor = (cargo: string | null) =>
  cargo === CARGO_CODES.CORRETOR || cargo === CARGO_CODES.CORRETOR_ALT;

export const isGerenteVendas = (cargo: string | null) =>
  cargo === CARGO_CODES.GERENTE_VENDAS ||
  cargo === CARGO_CODES.GERENTE_VENDAS_ALT;

export const isSuperintendente = (cargo: string | null) =>
  cargo === CARGO_CODES.SUPERINTENDENTE;

export const canViewEquipeColumn = (
  cargo: string | null,
  usuarioGerenteOnline = false
) => isSuperintendente(cargo) || usuarioGerenteOnline;

export const isNonCorretor = (cargo: string | null) => !isCorretor(cargo);

export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  alwaysVisible?: boolean;
}

export const getDefaultColumns = (
  codigoCargo: string | null,
  usuarioGerenteOnline = false
): ColumnConfig[] => [
  { id: "select", label: "Select", visible: true, alwaysVisible: true },
  { id: "actions", label: "Ações", visible: true, alwaysVisible: true },
  { id: "lead", label: "Lead", visible: true, alwaysVisible: true },
  { id: "etapa", label: "Etapa", visible: true },
  { id: "subetapa", label: "Subetapa", visible: true },
  { id: "obs", label: "Obs", visible: true },
  {
    id: "ultimaTarefa",
    label: "Última Tarefa",
    visible: isCorretor(codigoCargo),
  },
  { id: "telefone", label: "Telefone", visible: true },
  { id: "email", label: "E-mail", visible: isCorretor(codigoCargo) },
  {
    id: "corretorResponsavel",
    label: "Corretor responsável",
    visible: isNonCorretor(codigoCargo),
  },
  {
    id: "equipe",
    label: "Equipe",
    visible: canViewEquipeColumn(codigoCargo, usuarioGerenteOnline),
  },
  {
    id: "ultimoResponsavel",
    label: "Último Responsável",
    visible: isGerenteVendas(codigoCargo),
  },
  { id: "produto", label: "Produto", visible: true },
  { id: "etiqueta", label: "Etiqueta", visible: isCorretor(codigoCargo) },
  { id: "dataCriacao", label: "Criação", visible: true },
  { id: "expiraEm", label: "Expira em", visible: true },
  { id: "dataAtualizacao", label: "Atualização", visible: true },
  { id: "temperatura", label: "Temp.", visible: true },
];

export const COLUMN_WIDTHS: Record<string, number> = {
  select: 36,
  lead: 120,
  etapa: 120,
  subetapa: 120,
  obs: 70,
  ultimaTarefa: 60,
  telefone: 160,
  email: 165,
  corretorResponsavel: 165,
  ultimoResponsavel: 165,
  equipe: 165,
  produto: 95,
  etiqueta: 100,
  dataCriacao: 100,
  expiraEm: 80,
  dataAtualizacao: 125,
  temperatura: 90,
};

export interface SortConfig {
  ordenacaoLeads: number;
  orderKey: string;
}

export const SORT_MAP: Record<string, SortConfig> = {
  lead: { ordenacaoLeads: 1, orderKey: "order1" },
  obs: { ordenacaoLeads: 2, orderKey: "order2" },
  telefone: { ordenacaoLeads: 3, orderKey: "order3" },
  email: { ordenacaoLeads: 4, orderKey: "order4" },
  corretorResponsavel: { ordenacaoLeads: 10, orderKey: "order4" },
  ultimoResponsavel: { ordenacaoLeads: 10, orderKey: "order4" },
  equipe: { ordenacaoLeads: 11, orderKey: "order4" },
  etapa: { ordenacaoLeads: 6, orderKey: "order6" },
  dataAtualizacao: { ordenacaoLeads: 7, orderKey: "order7" },
  expiraEm: { ordenacaoLeads: 8, orderKey: "order8" },
  temperatura: { ordenacaoLeads: 9, orderKey: "order9" },
  dataCriacao: { ordenacaoLeads: 12, orderKey: "order12" },
  etiqueta: { ordenacaoLeads: 13, orderKey: "order13" },
};

export const FAROL_COLORS: Record<
  string,
  { background: string; text: string }
> = {
  amarelo: { background: "#FCE1CC", text: "#ED6C02" },
  verde: { background: "#E8F9E8", text: "#00a94c" },
  vermelho: { background: "#f4363620", text: "#f43636" },
};

export const DEFAULT_FAROL_COLORS = { background: "#ECECEC", text: "#333333" };

// Quantidade por página usada apenas quando o usuário ainda não escolheu uma.
export const DEFAULT_TOTAL_ITENS_RETORNO = 100;

// A quantidade por página é uma preferência do usuário: limpar ou aplicar
// filtros nunca deve sobrescrevê-la. Lê do estado atual e, quando ele ainda não
// existe (ex.: navegação vinda dos indicadores), cai no último valor salvo.
export const getTotalItensRetorno = (filters?: any): number => {
  const fromFilters = Number(filters?.paginationParams?.TotalItensRetorno);
  if (fromFilters > 0) return fromFilters;

  try {
    const stored = localStorage.getItem("@sell.it/leadFilters");
    const parsed = stored ? JSON.parse(stored) : null;
    const fromStorage = Number(parsed?.paginationParams?.TotalItensRetorno);
    if (fromStorage > 0) return fromStorage;
  } catch {
    // localStorage indisponível ou corrompido — usa o padrão
  }

  return DEFAULT_TOTAL_ITENS_RETORNO;
};

export const INITIAL_FILTERS = {
  OrdenarPorRecentes: null,
  InicioIntervaloCadastroLead: null,
  FimIntervaloCadastroLead: null,
  InicioIntervaloAtualizacaoLead: null,
  FimIntervaloAtualizacaoLead: null,
  InicioIntervaloTransferencia: null,
  FimIntervaloTransferencia: null,
  FiltrarRecebidosPorTransferencia: null,
  InicioIntervaloValorImovel: "",
  FimIntervaloValorImovel: "",
  paginationParams: {
    PaginaAtual: 1,
    PalavrasChave: "",
    TotalItensRetorno: DEFAULT_TOTAL_ITENS_RETORNO,
    OrdenarPorOrdemAlfabetica: "",
  },
  OrdenacaoLeads: null,
  OrdenacaoCrescente: undefined,
  order1: null,
  order2: null,
  order3: null,
  order4: null,
  order5: null,
  order6: null,
  order7: null,
  order8: null,
  order13: null,
};

export const formatPhoneNumber = (str: string): string => {
  const match: RegExpMatchArray | null =
    str.match(/^(\d{2})(\d{2})(\d{5})(\d{4})$/) ||
    str.match(/^(\d{2})(\d{2})(\d{4})(\d{4})$/) ||
    str.match(/^(\d{3})(\d{3})(\d{3})(\d{3})(\d{3})$/) ||
    str.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/) ||
    str.match(/^(\d{2})(\d{3})(\d{3})(\d{3})(\d{1})$/);

  if (match && match.length <= 5) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
  }
  if (match && match.length > 5) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}-${match[5]}`;
  }

  return str;
};

export const getFarolColors = (
  farol: string,
  type: "background" | "text"
): string => {
  const colors = FAROL_COLORS[farol] || DEFAULT_FAROL_COLORS;
  return colors[type];
};
